/** One kept run in Past runs: who asked and when, what the customer last said, and what kind of run it was. */

import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { KindBadge } from '@/modules/back-testing/kind-badge';
import { DescribedBadge } from '../describe/described-badge';
import { MODE_LABELS, MODE_TONES } from '@/modules/back-testing/runs/modes';
import type { SimulatorRun } from '@/services/simulator-run.service';
import { ListRow } from '../conversations/conversation-parts';

const TAG_TONES = {
  muted: 'border-gray-500 text-gray-800',
  red: 'border-red-200 bg-red-50 text-red-700',
} as const;

const Tag = ({
  tone = 'muted',
  className,
  children,
}: {
  tone?: keyof typeof TAG_TONES;
  className?: string;
  children: ReactNode;
}) => <span className={cn('rounded-full border px-1.5 text-[10px] leading-4', TAG_TONES[tone], className)}>{children}</span>;

const lastCustomerTurn = (run: SimulatorRun): string =>
  [...run.query].reverse().find((turn) => turn.role === 'user')?.text ?? '';

const RunTags = ({ run }: { run: SimulatorRun }) => (
  <span className='flex flex-wrap items-center gap-1 pt-1'>
    <Tag className={MODE_TONES[run.mode]}>{MODE_LABELS[run.mode]}</Tag>
    {run.scenarioId && <Tag>Scenario</Tag>}
    <KindBadge kind={run.kind} className='px-1.5 py-0 leading-4' />
    {!run.scenarioId && run.scenarioType && <DescribedBadge className='px-1.5 py-0 leading-4' />}
    {run.ticketId && <Tag className='font-mono'>#{run.ticketId}</Tag>}
    {run.error && <Tag tone='red'>Failed</Tag>}
  </span>
);

interface PastRunRowProps {
  run: SimulatorRun;
  author: string;
  selected: boolean;
  onSelect: () => void;
}

export const PastRunRow = ({ run, author, selected, onSelect }: PastRunRowProps) => (
  <ListRow
    title={author}
    when={run.createdAt}
    preview={lastCustomerTurn(run)}
    meta={<RunTags run={run} />}
    selected={selected}
    onSelect={onSelect}
  />
);
